import { apiFetch } from './client';
import { putToPresignedUrl } from './uploads';

export const EVENT_ERRORS = {
  name_required: 'Event name is required.',
  invalid_schedule: 'End time must be after start time.',
  invalid_pricing_mode: 'Choose a valid pricing mode.',
  discount_percent_required: 'Enter a discount percent for percent pricing.',
  coupon_code_required: 'Enter a coupon code for coupon pricing.',
};

export function normalizeEvent(raw) {
  const event = raw?.event || raw || {};
  const itemIds = (event.items || event.item_ids || []).map((item) => (
    typeof item === 'string' ? item : item.item_id || item.id
  ));
  return {
    id: event.event_id || event.id,
    name: event.name || '',
    emoji: event.emoji || '',
    sortOrder: event.sort_order ?? 1,
    description: event.description || '',
    status: event.status || 'ACTIVE',
    pricingMode: event.pricing_mode || 'NONE',
    discountPercent: event.discount_percent ?? '',
    couponCode: event.coupon_code || '',
    startsAt: event.starts_at || null,
    endsAt: event.ends_at || null,
    imageUrl: event.image_url || '',
    itemIds,
  };
}

function toEventPayload(data) {
  const payload = {
    name: data.name?.trim() || undefined,
    event_id: data.eventId?.trim() || undefined,
    emoji: data.emoji?.trim() || undefined,
    sort_order: data.sortOrder !== undefined && data.sortOrder !== '' ? Number(data.sortOrder) : undefined,
    description: data.description?.trim() || undefined,
    status: data.status || undefined,
    pricing_mode: data.pricingMode || undefined,
    discount_percent: data.pricingMode === 'PERCENT' && data.discountPercent !== '' && data.discountPercent !== undefined
      ? Number(data.discountPercent) : undefined,
    coupon_code: data.pricingMode === 'COUPON' && data.couponCode
      ? data.couponCode.trim().toUpperCase() : undefined,
    starts_at: data.startsAt || undefined,
    ends_at: data.endsAt || undefined,
  };
  Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);
  return payload;
}

export async function listEvents(token) {
  const raw = await apiFetch('/events', { token });
  const list = raw?.events || (Array.isArray(raw) ? raw : []);
  return list.map(normalizeEvent);
}

export async function getEvent(id, token) {
  const raw = await apiFetch(`/events/${encodeURIComponent(id)}`, { token });
  return normalizeEvent(raw);
}

export async function getEventItems(id, token) {
  const raw = await apiFetch(`/events/${encodeURIComponent(id)}/items`, { token });
  const items = raw?.items || (Array.isArray(raw) ? raw : []);
  return items.map((item) => item.item_id || item.id);
}

export async function createEvent(data, token) {
  const raw = await apiFetch('/events', { method: 'POST', token, body: toEventPayload(data) });
  return normalizeEvent(raw);
}

export async function updateEvent(id, data, token) {
  const raw = await apiFetch(`/events/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    token,
    body: toEventPayload(data),
  });
  return normalizeEvent(raw);
}

export async function setEventItems(id, itemsInput, token, percentOff = null) {
  const itemsPayload = (itemsInput || []).map((item) => {
    if (typeof item === 'string') {
      const obj = { item_id: item };
      if (percentOff !== null && percentOff !== undefined && percentOff !== '') {
        obj.percent_off = Number(percentOff);
      }
      return obj;
    }
    return {
      item_id: item.item_id || item.id,
      ...(item.percent_off !== undefined ? { percent_off: Number(item.percent_off) } : (percentOff ? { percent_off: Number(percentOff) } : {})),
    };
  });

  await apiFetch(`/events/${encodeURIComponent(id)}/items`, {
    method: 'PUT',
    token,
    body: { items: itemsPayload },
  });
}

async function presignEventImage(id, contentType, token) {
  const raw = await apiFetch(`/events/${encodeURIComponent(id)}/image/presign`, {
    method: 'POST',
    token,
    body: { content_type: contentType },
  });
  return { uploadUrl: raw.upload_url, s3Key: raw.s3_key };
}

export async function uploadEventBanner(id, file, token) {
  const { uploadUrl, s3Key } = await presignEventImage(id, file.type || 'image/png', token);
  await putToPresignedUrl(uploadUrl, file);
  const raw = await apiFetch(`/events/${encodeURIComponent(id)}/image/confirm`, {
    method: 'POST',
    token,
    body: { s3_key: s3Key },
  });
  return normalizeEvent(raw);
}

export async function deleteEventBanner(id, token) {
  const raw = await apiFetch(`/events/${encodeURIComponent(id)}/image`, {
    method: 'DELETE',
    token,
  });
  return raw ? normalizeEvent(raw) : null;
}

export async function removeEventItem(id, itemId, token) {
  await apiFetch(`/events/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}`, {
    method: 'DELETE',
    token,
  });
}

export async function saveEvent(data, token, { editingId, previousItemIds = [] } = {}) {
  // Step 1: Create or Update event metadata (POST /events)
  let event = editingId
    ? await updateEvent(editingId, data, token)
    : await createEvent(data, token);

  // Step 2: If image file selected, presign -> S3 PUT -> confirm
  if (data.removeBanner && event.imageUrl) {
    const cleared = await deleteEventBanner(event.id, token);
    event = cleared || { ...event, imageUrl: '' };
  } else if (data.imageFile) {
    event = await uploadEventBanner(event.id, data.imageFile, token);
  }

  // Step 3: Attach products via PUT /events/{id}/items
  const nextIds = data.itemIds || [];
  const prevIds = previousItemIds || [];
  const itemsChanged = nextIds.length !== prevIds.length
    || nextIds.some((id) => !prevIds.includes(id))
    || !editingId;

  if (itemsChanged && nextIds.length > 0) {
    const percentOff = data.pricingMode === 'PERCENT' ? data.discountPercent : null;
    await setEventItems(event.id, nextIds, token, percentOff);
    event = { ...event, itemIds: nextIds };
  }

  return event;
}
